import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import { UTM_SOURCES } from 'common/constants';
import {
  BlueLink,
  ColoredButton,
  Div,
  H1,
  MarketingButton,
  P,
  VisuallyHidden,
} from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';
import { requestAppointment } from 'connect/actions';
import { CHECKOUT_ERROR_TYPES } from 'connect/constants';
import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';
import {
  selectDataAppointment,
  selectDataCheckoutFormData,
  selectDataClinic,
  selectDataLoadingUser,
  selectDataMatchInsuranceId,
  selectDataPatient,
  selectDataProfileLink,
  selectDataRequestedAppointment,
  selectDataUser,
} from 'connect/selectors';
import { UserSourceStateHandler } from 'connect/state/handlers/user';
import { Form, Icon } from 'opencare-crown';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useRedirectionService } from 'services/RedirectionService';
import cjAffiliate from 'utils/cjAffiliate';
import { googlePlaceActionLink } from 'utils/google';

import CONFIG from '../../../../../../app.config';
import {
  getPatientAge,
  getPatientSexAtBirth,
} from '../../../../../utils/patient';
import Error from '../../../../common/components/Error/Error';
import { getTimeFromStartTime } from '../../../../common/helpers/appointment-slot-helpers';
import { getInNetworkStatus } from '../../../../common/helpers/common-helpers';
import { getMatchResultsPageConfiguration } from '../../../../funnel/components/Search/constants';
import { ALL_CONFIGURATIONS } from '../../../../funnel/funnelConfigs';
import { getFunnelConfiguration } from '../../../../funnel/storage/constants';
import { useFunnelStorage } from '../../../../funnel/storage/Storage';
import AddPromoCodeModal from '../../../../results/components/AddPromoCodeModal/AddPromoCodeModal';
import {
  ConfirmationSubHeading,
  Link,
  StepLoading,
  StyledLock,
} from '../../../checkout-styled-components';
import {
  makeSelectInsuranceData,
  makeSelectRequestData,
  selectAnyProviderSelected,
  selectAppointmentRequestedId,
  selectDataCheckoutFormErrorStatus,
  selectOngoingCareNoMatch,
  selectReasonNoMatch,
  selectRequestedProvider,
  selectRequestedSlot,
  selectSpecialtyId,
} from '../../../connect/selectors';
import {
  getOfferedAppointmentType,
  getPatientAppUrl,
} from '../../../helpers/confirmation-helpers';
import AppointmentCard from '../../common/AppointmentCard/AppointmentCard';
import AvailabilityCard from '../../common/AvailabilityCard/AvailabilityCard';
import ClinicCard from '../../common/ClinicCard/ClinicCard';
import ContactDetailsCard from '../../common/ContactDetailsCard/ContactDetailsCard';
import PromoCodeCard from '../../common/PromoCodeCard/PromoCodeCard';
import ProviderCard from '../../common/ProviderCard/ProviderCard';

const analytics = new Analytics();

const TERMS_URL = `${CONFIG.SEARCH_APP_URL}/terms/sms`;
const PRIVACY_URL = `${CONFIG.SEARCH_APP_URL}/privacy`;
const PATIENT_URL = `${CONFIG.PATIENT_APP_URL}/#/appointments`;

const ConfirmationStep = ({ close }) => {
  const funnelStorage = useFunnelStorage();

  const analyticsService = useAnalyticsService();
  const redirectionService = useRedirectionService();

  const [isInitializing, setIsInitializing] = useState(true);
  const [notes, setNotes] = useState('');
  const [addPromoCodeModalIsVisible, setAddPromoCodeVisibility] = useState(
    false,
  );
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const clinic = useSelector(selectDataClinic);
  const provider = useSelector(selectRequestedProvider);
  const anyProviderSelected = useSelector(selectAnyProviderSelected);
  const requestedSlot = useSelector(selectRequestedSlot);
  const specialtyId = useSelector(selectSpecialtyId);
  const requestData = useSelector(makeSelectRequestData);
  const checkoutFormData = useSelector(selectDataCheckoutFormData);
  const ongoingCareNoMatch = useSelector(selectOngoingCareNoMatch);
  const reasonNoMatch = useSelector(selectReasonNoMatch);
  const insuranceData = useSelector(makeSelectInsuranceData);
  const appointment = useSelector(selectDataAppointment);
  const appointmentId = useSelector(selectAppointmentRequestedId);
  const referralPromotion = useSelector(
    RequestReferralPromotion.value.selector,
  );
  const user = useSelector(selectDataUser);
  const loadingUser = useSelector(selectDataLoadingUser);
  const userSource = useSelector(UserSourceStateHandler.selector);
  const patient = useSelector(selectDataPatient);
  const insurancePlanId = useSelector(selectDataMatchInsuranceId);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const checkoutFormErrorStatus = useSelector(
    selectDataCheckoutFormErrorStatus,
  );
  const directLink = useSelector(selectDataProfileLink);
  const [showProviderCard, setShowProviderCard] = useState(
    !!provider && !anyProviderSelected,
  );
  const [showClinicCard, setShowClinicCard] = useState(
    !!clinic.id && anyProviderSelected,
  );

  useEffect(() => {
    setIsInitializing(loadingUser);
  }, [loadingUser]);

  useEffect(() => {
    if (appointmentId) {
      const matchResultsPageConfigId =
        appointment?.match?.matchResultsPageConfiguration?.id
        || getMatchResultsPageConfiguration()?.id;

      analytics.trackConversion({
        pageType: analytics.pageTypes.FORM,
        pageName: clinic.name,
        name: 'form-booking',
        section: 'confirm',
        content: {
          first: _.get(patient, 'firstName'),
          last: _.get(patient, 'lastName'),
          birth: _.get(patient, 'dateOfBirth'),
          email: _.get(patient, 'email'),
          phone: _.get(patient, 'mobilePhone'),
          promo_code: _.get(requestData.dmProps, 'promo_code'),
          date: _.get(requestedSlot, 'date'),
          time:
            requestedSlot && getTimeFromStartTime(requestedSlot.time, 'HHmm'),
          reco_appointment_booked: `${true}`,
          reco_step_reached: 'Booked',
        },
        userId: _.get(user, 'id'),
        patientId: _.get(patient, 'id'),
        source: requestData?.source,
        appointmentId,
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        partnerId: _.get(user, 'partner.id'),
        isReturningPatient: checkoutFormData.isReturningPatient,
        heapUser: {
          age: getPatientAge(patient),
          gender: getPatientSexAtBirth(patient, appointment),
          funnel_config_id: appointment?.match?.funnelConfiguration?.id,
          match_config_id: appointment?.match?.configuration,
          match_results_page_config_id: matchResultsPageConfigId,
        },
        heapEvent: {
          funnel_config_id: appointment?.match?.funnelConfiguration?.id,
          match_id: appointment?.match?.id,
          match_config_id: appointment?.match?.configuration,
          match_results_page_config_id: matchResultsPageConfigId,
          user_id: user?.id,
          offer_value: appointment?.offer?.cost,
        },
      });

      cjAffiliate.trackAppointmentConversion(appointment);

      // Clear marketing values in funnel now that they've been attached to an appointment
      funnelStorage.clearMarketingCache();

      // Clear the prefill data.
      funnelStorage.clearPrefillData();

      googlePlaceActionLink.resetConversionData();

      analytics.waitForQueue().then(() => {
        setLoading(false);
        redirectionService.redirect({
          url: getPatientAppUrl(appointmentId),
          reason: 'appointment-confirmation',
        });
      });
    }
  }, [appointmentId]);

  useEffect(() => {
    if (provider) {
      setShowProviderCard(true);
    }
  }, [provider]);

  useEffect(() => {
    if (clinic && anyProviderSelected) {
      setShowClinicCard(true);
      setShowProviderCard(false);
    }
  }, [clinic]);

  useEffect(() => {
    if (checkoutFormErrorStatus) {
      setLoading(false);
      setError(checkoutFormErrorStatus);
    }
  }, [checkoutFormErrorStatus]);

  useEffect(() => {
    if (requestedAppointment.providerId) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.FORM,
        pageName: clinic.name,
        name: 'form-booking',
        section: 'confirm',
        practiceId: requestedAppointment.provider?.clinic,
        providerId: requestedAppointment.providerId,
        ...analyticsService.analyticsData,
      });
    }
  }, [requestedAppointment]);

  const handleCheckAvailability = () => {
    if (
      (!error || error === CHECKOUT_ERROR_TYPES.GENERIC)
      && requestedAppointment.providerId
    ) {
      setLoading(true);

      const offeredAppointmentType = getOfferedAppointmentType(
        provider,
        specialtyId,
      );
      requestData.note = notes || '';
      requestData.inNetwork = getInNetworkStatus(
        insurancePlanId,
        clinic.insurancePlans,
      );
      requestData.isReturningPatient =
        checkoutFormData.isReturningPatient || false;

      // remove undefined properties of requestData for users coming to the clinic profile from a direct link
      if (directLink && userSource !== UTM_SOURCES.MEMBER_ID_REMINDER) {
        requestData.source = 'oc_direct';
        delete requestData.reason;
        delete requestData.match;
        delete requestData.trafficSource;
        delete requestData.trafficSourceLastTouch;
      }

      // Update with no match reason if no match present
      if (!_.get(requestData, 'match')) {
        if (ongoingCareNoMatch) {
          requestData.ongoingCare = ongoingCareNoMatch;
        }

        if (reasonNoMatch) {
          requestData.reason = {
            overall: {}, // This field is required in the backend
            selectedTreatments: reasonNoMatch,
          };
        }
      }

      const {
        rwgToken,
        merchantId,
      } = googlePlaceActionLink.getConversionData();

      if (rwgToken && merchantId) {
        // Do not make the hidden copy like `requestData.googleActions = googlePlaceActionLink.getConversionData()`
        // because this may produce many errors when the data schemas change.
        requestData.googleActions = {
          rwgToken,
          merchantId,
        };
        requestData.source = 'google_place_action';
      }

      dispatch(
        requestAppointment(
          requestedAppointment.providerId,
          requestedSlot,
          requestData,
          _.get(offeredAppointmentType, 'id'),
          insuranceData,
          getFunnelConfiguration(funnelStorage, ALL_CONFIGURATIONS),
          anyProviderSelected,
        ),
      );
    }
  };

  const renderErrorMessage = () => {
    if (error) {
      return (
        <Error style={{ marginBottom: '30px' }}>
          <P color={colors.danger} data-testid={error}>
            {error === CHECKOUT_ERROR_TYPES.APPOINTMENT_REQUESTED_ALREADY
              && (
                <>
                  You have already submitted an appointment request with this
                  clinic. <BlueLink href={PATIENT_URL}>Click here</BlueLink> to
                  manage your appointment.
                </>
              )
            }

            {error === CHECKOUT_ERROR_TYPES.NOT_PATIENT
              && (
                <>
                  You must log in as a patient to book an appointment. The email
                  address entered is associated with a clinic or provider.
                </>
              )
            }

            {error === CHECKOUT_ERROR_TYPES.PROVIDER_NO_ONLINE_BOOKING
              && (
                <>
                  Provider decided to disable their requests for the time being,
                  please select{' '}
                  <BlueLink href="/results">another practice.</BlueLink>
                </>
              )
            }

            {error === CHECKOUT_ERROR_TYPES.PROVIDER_NO_RESERVED_SLOTS
              && (
                <>
                  There was an issue with this practice's calendar, please select{' '}
                  <BlueLink href="/results">another practice.</BlueLink>
                </>
              )
            }

            {error === CHECKOUT_ERROR_TYPES.GENERIC
              && <>Something went wrong while submitting your details.</>
            }
          </P>
        </Error>
      );
    }

    return null;
  };

  if (isInitializing || !_.get(clinic, 'id')) {
    return (
      <div data-testid="loading">
        <StepLoading type="loading" />
      </div>
    );
  }

  // Replace entire confirm window if the requested time slot is already taken
  if (error === CHECKOUT_ERROR_TYPES.SLOT_TAKEN) {
    return (
      <Div>
        <Icon
          type="exclamation-circle"
          style={{
            display: 'block',
            margin: '0 auto',
            color: colors.coral,
            fontSize: '32px',
          }}
        />
        <P
          color={colors.slate}
          textAlign="center"
          margin="15px 0"
          data-testid={CHECKOUT_ERROR_TYPES.SLOT_TAKEN}
        >
          Looks like someone already took the timeslot that you just tried to
          book.
        </P>
        <ColoredButton
          size="large"
          block
          color={colors.white}
          background={colors.blue}
          className="close-modal-button"
          htmlType="button"
          onClick={close}
        >
          Choose a different time
        </ColoredButton>
      </Div>
    );
  }

  return (
    <Form>
      {renderErrorMessage()}
      <H1 fontSize="20px" margin="-12px 0 0 auto">
        Review Your Request
        <StyledLock
          aria-hidden
          bottom="5px"
          margin="0 0 0 8px"
          size="16px"
          theme="filled"
          type="lock"
          role="presentation"
        />
      </H1>
      <P color={colors.slate} margin="0 0 20px">
        Please ensure the information below is correct before submission to your
        new dentist.
      </P>
      <div
        aria-label={requestedSlot ? 'Appointment' : 'Availability Preferences'}
      >
        {requestedSlot ? <AppointmentCard /> : <AvailabilityCard />}
      </div>
      <ConfirmationSubHeading
        color={colors.sageDark}
        margin="10px,0,10px,-10px"
        aria-hidden
      >
        {showClinicCard ? 'Clinic' : 'Provider'}
      </ConfirmationSubHeading>
      {showProviderCard
        && (
          <ProviderCard
            provider={provider}
            hasInsurance={insurancePlanId}
            inNetwork={getInNetworkStatus(insurancePlanId, clinic.insurancePlans)}
          />
        )
      }
      {showClinicCard
        && (
          <ClinicCard
            clinic={clinic}
            hasInsurance={insurancePlanId}
            inNetwork={getInNetworkStatus(insurancePlanId, clinic.insurancePlans)}
          />
        )
      }
      <ConfirmationSubHeading
        color={colors.sageDark}
        margin="10px,0,10px,-10px"
        aria-hidden
      >
        Contact details
      </ConfirmationSubHeading>
      <ContactDetailsCard setNotes={setNotes} />
      {!referralPromotion
        && <PromoCodeCard setAddPromoCodeVisibility={setAddPromoCodeVisibility} />
      }
      <MarketingButton
        type="primary"
        shape="round"
        size="large"
        block
        className="patient-form-button"
        data-testid="confirmationButton"
        htmlType="button"
        loading={isLoading}
        onClick={handleCheckAvailability}
      >
        Send Request
      </MarketingButton>
      <P
        margin="15px 0 0"
        fontSize="12px"
        color={colors.gray8}
        textAlign="center"
      >
        <VisuallyHidden>
          By requesting an appointment on Opencare, you agree to our Terms of
          Service and Privacy Policy.
        </VisuallyHidden>
        <span aria-hidden>
          By requesting an appointment on Opencare, you agree to our{' '}
        </span>
        <Link href={TERMS_URL} target="_blank">
          Terms of Service
        </Link>{' '}
        <span aria-hidden>and</span>{' '}
        <Link href={PRIVACY_URL} target="_blank">
          Privacy Policy
        </Link>
        <span aria-hidden>.</span>
      </P>
      <AddPromoCodeModal
        addPromoCodeModalIsVisible={addPromoCodeModalIsVisible}
        setAddPromoCodeVisibility={setAddPromoCodeVisibility}
      />
    </Form>
  );
};

ConfirmationStep.propTypes = {
  close: PropTypes.func.isRequired,
};

export default ConfirmationStep;
